/**
 * Stale-bundle recovery after a deploy (2026-10-04, docs/features/ops.md "Stale tabs after a deploy").
 * Pure helpers, identical in both apps; the side effects live in appUpdate.ts.
 *
 * A tab or shell opened before a deploy still runs the old main bundle, which asks for chunk
 * file names the new build may not have. Unhandled, React unmounted everything: a white screen
 * until the app was killed. Deploys now keep retired chunks for 14 days (deploy.yml), a failed
 * chunk reloads the page once, and a resumed app compares its main bundle with the server's.
 */

/** webpack's ChunkLoadError / mini-css-extract's CSS chunk failure / a native dynamic import failure. */
export function isChunkLoadError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: unknown; message?: unknown; code?: unknown };
  if (e.name === 'ChunkLoadError' || e.code === 'CSS_CHUNK_LOAD_FAILED') return true;
  const msg = typeof e.message === 'string' ? e.message : '';
  return /Loading (CSS )?chunk \S+ failed/i.test(msg)
    || /Failed to fetch dynamically imported module/i.test(msg)
    || /error loading dynamically imported module/i.test(msg);
}

const MAIN_RE = /\/static\/js\/main\.([0-9a-f]{6,})\.js/;

/** The content hash of the main bundle referenced by an HTML document, or null (dev server, tests). */
export function mainBundleHash(html: string): string | null {
  const m = MAIN_RE.exec(html);
  return m ? m[1] : null;
}

/** Same, for the running page: its own <script src=".../main.<hash>.js">. */
export function runningBundleHash(doc: Document): string | null {
  for (const s of Array.from(doc.scripts)) {
    const h = s.src ? mainBundleHash(s.src) : null;
    if (h) return h;
  }
  return null;
}

/** One automatic reload per this window: a second chunk failure within it shows the error screen instead. */
export const RELOAD_GUARD_MS = 60_000;

export function mayAutoReload(lastReloadAt: number | null, now: number): boolean {
  return lastReloadAt === null || !Number.isFinite(lastReloadAt) || now - lastReloadAt > RELOAD_GUARD_MS;
}

/**
 * When a newer build is found on resume: reload silently only if nothing can be lost —
 * the app was away long enough that the user is starting over, on a list-like screen,
 * with no dialog open and nothing playing. Everything else gets the "update available" toast.
 */
export const SILENT_RELOAD_AFTER_HIDDEN_MS = 30 * 60_000;

export function shouldReloadSilently(opts: {
  hiddenForMs: number;
  pathname: string;
  safePaths: readonly string[];
  dialogOpen: boolean;
  mediaPlaying: boolean;
}): boolean {
  return opts.hiddenForMs >= SILENT_RELOAD_AFTER_HIDDEN_MS
    && opts.safePaths.includes(opts.pathname)
    && !opts.dialogOpen
    && !opts.mediaPlaying;
}
